import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { ASSET } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { useCart } from "../../contexts/CartContext";
import { ShoppingBag, Store, ArrowLeft, Boxes, Package, Loader2, ZoomIn, X } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1553546895-531931aa1aa8?w=900&q=70";

export default function VendorProduct() {
  const { id } = useParams();
  const { formatPrice } = useLang();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState(false);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/vendor/products/${id}`)
      .then((r) => {
        setProduct(r.data.product);
        setStore(r.data.store);
        setVariant(r.data.product?.variants?.length ? r.data.product.variants[0] : null);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="container-xl py-24 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (notFound || !product) {
    return (
      <div className="container-xl py-24 text-center">
        <h1 className="editorial-heading text-3xl mb-2">Prodotto non trovato</h1>
        <Link to="/negozi" className="text-[hsl(var(--primary))] hover:underline">← Tutte le vetrine</Link>
      </div>
    );
  }

  const isBlocks = product.sale_mode === "blocchi";
  const img = product.image_url ? ASSET(product.image_url) : FALLBACK;
  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const isCaviar = product.category === "Caviale" || store?.store_slug === "cutcaviar";
  const currentPrice = variant ? variant.price : product.price;

  const addToCart = () => {
    add({
      product_id: product.id,
      product_type: "vendor",
      name: variant ? `${product.name} · ${variant.label}` : product.name,
      image: img,
      unit_price: currentPrice,
      quantity: qty,
      unit: variant ? variant.label : "pz",
    });
  };

  return (
    <div data-testid="vendor-product-page" className="container-xl py-10">
      <Link to={store?.store_slug ? `/negozio/${store.store_slug}` : "/negozi"} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(var(--primary))] mb-6">
        <ArrowLeft className="h-4 w-4" /> {store?.store_name || "Torna alla vetrina"}
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="md:sticky md:top-24 self-start">
          <button
            type="button"
            onClick={() => setZoom(true)}
            data-testid="vp-image-zoom"
            className="group relative block w-full max-w-sm mx-auto aspect-[4/3] overflow-hidden rounded-sm bg-[hsl(var(--muted))] border border-border cursor-zoom-in"
            title="Clicca per ingrandire"
          >
            <img src={img} alt={product.name} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK; }} className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 ${isCaviar ? "scale-[1.45] object-[center_70%] group-hover:scale-[1.55]" : ""}`} />
            <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 text-[11px] uppercase tracking-[0.15em] font-semibold rounded-sm ${isBlocks ? "bg-[#0a0a0a] text-[#C9A961]" : "bg-[hsl(var(--primary))] text-white"}`}>
              {isBlocks ? <Boxes className="h-3 w-3" /> : <Package className="h-3 w-3" />}
              {isBlocks ? "B2B · a blocchi" : "B2C · a pezzi"}
            </span>
            <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full bg-white/90 text-stone-800 opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn className="h-3.5 w-3.5" /> Zoom
            </span>
          </button>
        </div>
        <div>
          {product.category && <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">{product.category}</div>}
          <h1 className="editorial-heading text-4xl mb-3">{product.name}</h1>
          {store && (
            <Link to={`/negozio/${store.store_slug}`} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--primary))] hover:underline mb-4">
              <Store className="h-4 w-4" /> {store.store_name}
            </Link>
          )}
          <div className="text-3xl font-semibold text-[hsl(var(--primary))] mt-2" data-testid="vp-price">{formatPrice ? formatPrice(currentPrice) : `€ ${currentPrice}`}{hasVariants && variant && <span className="text-base font-normal text-muted-foreground"> / {variant.label}</span>}</div>
          {isBlocks && <div className="text-sm text-muted-foreground mt-1">{product.block_size} pezzi/blocco · minimo {product.min_order_blocks || 1} blocchi</div>}
          <div className={`mt-2 text-sm font-semibold ${product.quantity > 0 ? "text-emerald-600" : "text-destructive"}`}>{product.quantity > 0 ? `${product.quantity} disponibili` : "Esaurito"}</div>
          {product.description && <p className="mt-5 text-foreground/80 leading-relaxed whitespace-pre-line">{product.description}</p>}

          {hasVariants && !isBlocks && (
            <div className="mt-6">
              <label htmlFor="vp-variant" className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Grammatura</label>
              <select
                id="vp-variant"
                data-testid="vp-variant-select"
                value={variant ? variant.label : ""}
                onChange={(e) => setVariant(product.variants.find((v) => v.label === e.target.value) || product.variants[0])}
                className="w-full sm:w-64 px-4 py-3 border border-border rounded-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30"
              >
                {product.variants.map((v) => (
                  <option key={v.label} value={v.label}>
                    {v.label} — {formatPrice ? formatPrice(v.price) : `€ ${v.price}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isBlocks ? (
            <Link to={store?.store_slug ? `/negozio/${store.store_slug}` : "/negozi"} className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#C9A961] text-[#8a6d1f] rounded-sm text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#C9A961]/10">
              <Store className="h-4 w-4" /> Prodotto B2B · richiedi in vetrina
            </Link>
          ) : (
            <div className="mt-8 flex items-center gap-3">
              <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} className="w-20 px-3 py-3 border border-border rounded-sm" data-testid="vp-qty" />
              <button data-testid="vp-add-to-cart" onClick={addToCart} disabled={product.quantity <= 0} className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-[hsl(var(--primary))] text-white rounded-sm text-sm uppercase tracking-[0.15em] font-semibold hover:opacity-90 disabled:opacity-50">
                <ShoppingBag className="h-4 w-4" /> Aggiungi al carrello
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Zoom lightbox */}
      {zoom && (
        <div
          data-testid="vp-zoom-overlay"
          onClick={() => setZoom(false)}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <button onClick={() => setZoom(false)} aria-label="Chiudi" className="absolute top-5 right-5 text-white/80 hover:text-white">
            <X className="h-8 w-8" />
          </button>
          <img
            src={img}
            alt={product.name}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK; }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-sm shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
